import React, { useEffect, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Alert from "@mui/material/Alert";
import {
  ChatMessageCreatedSubscription,
  ChatMessageDeletedSubscription,
} from "core/subscriptions";
import { ChatMessagesQuery } from "core/queries";
import { useQuery } from "@apollo/client";
import { withSnackbar } from "notistack";
import ChatMessageListItemAdv from "core/components/ChatMessageListItemAdv";
import ChatMessageListItem from "core/components/ChatMessageListItem";
import LinearProgress from "@mui/material/LinearProgress";
import useWsLinkStatus from "core/hooks/useWsLinkStatus";
import { useTranslation } from "react-i18next";

const PREFIX = "ChatMessageList";

const classes = {
  root: `${PREFIX}-root`,
  list: `${PREFIX}-list`,
};

const Root = styled("div")(() => ({
  [`&.${classes.root}`]: {
    width: "100%",
    flex: 1,
    overflowX: "hidden",
    overflowY: "auto",
    overscrollBehavior: "contain",

    scrollBehavior: "smooth",

    msOverflowStyle: "none", // IE 10+
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Safari and Chrome
    },
  },

  [`& .${classes.list}`]: {},
}));

const WELCOME_DISMISSED_KEY = "chatWelcomeDismissed";

function ChatMessageList({
  limit,
  enqueueSnackbar,
  viewerIsAdmin,
  chatRoomId,
  viewer,
  onReply,
  onMention,
  onNewMessage,
}) {
  const { t } = useTranslation("core");
  const { online: wsConnected } = useWsLinkStatus();
  const listRef = useRef(null);

  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    if (typeof window === "undefined") return true;
    return sessionStorage.getItem(WELCOME_DISMISSED_KEY) === "true";
  });

  const handleDismissWelcome = () => {
    setWelcomeDismissed(true);
    sessionStorage.setItem(WELCOME_DISMISSED_KEY, "true");
  };

  const onNewMessageRef = useRef(onNewMessage);
  onNewMessageRef.current = onNewMessage;

  const onError = (error) => {
    enqueueSnackbar(error.message, {
      variant: "error",
    });
  };

  const { data, loading, error, subscribeToMore, refetch } = useQuery(
    ChatMessagesQuery,
    {
      fetchPolicy: "cache-and-network",
      skip: !process.browser,
      variables: {
        chatRoomId,
        limit,
      },
      onError,
    }
  );

  useEffect(() => {
    if (!chatRoomId) return;
    if (!process.browser) return;

    return subscribeToMore({
      document: ChatMessageCreatedSubscription,
      variables: {
        chatRoomId,
        userId: viewer?.id,
      },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        const newMessage = subscriptionData.data.chatMessageCreated;
        console.log(
          "[ChatMessageList] new message from subscription",
          newMessage
        );

        // Check if message already exists to prevent duplicates
        const messageExists = prev.chatMessages.nodes.some(
          (msg) => msg.id === newMessage.id
        );

        if (messageExists) {
          console.log(
            "[ChatMessageList] message already exists, skipping",
            newMessage.id
          );
          return prev;
        }

        if (onNewMessageRef.current) onNewMessageRef.current(newMessage);

        // Scroll to top (where new messages appear) after new message
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTop = 0;
          }
        }, 0);

        return Object.assign({}, prev, {
          chatMessages: {
            ...prev.chatMessages,
            count: prev.chatMessages.count + 1,
            nodes: [newMessage, ...prev.chatMessages.nodes],
          },
        });
      },
    });
  }, [chatRoomId]);

  useEffect(() => {
    if (!chatRoomId) return;
    if (!process.browser) return;

    return subscribeToMore({
      document: ChatMessageDeletedSubscription,
      variables: {
        chatRoomId,
      },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;

        return Object.assign({}, prev, {
          chatMessages: {
            ...prev.chatMessages,
            nodes: [
              ...prev.chatMessages.nodes.filter(
                (m) => m.id !== subscriptionData.data.chatMessageDeleted.id
              ),
            ],
          },
        });
      },
    });
  }, [chatRoomId]);

  // Polling fallback when WebSocket is disconnected
  useEffect(() => {
    if (wsConnected) return;
    if (!chatRoomId) return;

    const interval = setInterval(() => {
      refetch();
    }, 5000);

    return () => clearInterval(interval);
  }, [wsConnected, chatRoomId, refetch]);

  const updateCacheAfterDelete = (cache, { data: { deleteChatMessage } }) => {
    const result = cache.readQuery({
      query: ChatMessagesQuery,
      variables: {
        chatRoomId,
        limit,
      },
    });

    if (!result?.chatMessages) return;

    cache.writeQuery({
      query: ChatMessagesQuery,
      variables: {
        chatRoomId,
        limit,
      },
      data: {
        chatMessages: {
          ...result.chatMessages,
          nodes: result.chatMessages.nodes.filter(
            (m) => m.id !== deleteChatMessage.id
          ),
        },
      },
    });
  };

  const nodes = data?.chatMessages?.nodes || [];

  if (loading && !data) return <LinearProgress />;
  if (error) return null;

  return (
    <Root
      ref={listRef}
      className={classes.root}
      sx={{ backgroundColor: "background.body" }}
    >
      {!welcomeDismissed && (
        <Alert
          severity="info"
          icon={false}
          onClose={handleDismissWelcome}
          sx={{
            mx: 1,
            mt: 1,
            flexShrink: 0,
            bgcolor: (theme) =>
              theme.palette.primary.main +
              (theme.palette.mode === "dark" ? "1A" : "14"),
            color: "text.primary",
          }}
        >
          {t("chat.welcome")}
        </Alert>
      )}

      <List className={classes.list}>
        {nodes.map((message) => {
          if (message.sponsorChatMessage) {
            return (
              <ChatMessageListItemAdv
                key={message.id}
                sponsorChatMessage={message.sponsorChatMessage}
              />
            );
          }

          return (
            <ChatMessageListItem
              key={message.id}
              message={message}
              viewerIsAdmin={viewerIsAdmin}
              viewerIsCreator={viewer && viewer.id === message.userId}
              updateCacheAfterDelete={updateCacheAfterDelete}
              onReply={viewer ? onReply : undefined}
              onMention={viewer ? onMention : undefined}
            />
          );
        })}
      </List>
    </Root>
  );
}

ChatMessageList.defaultProps = {
  limit: 30,
};

ChatMessageList.propTypes = {
  limit: PropTypes.number.isRequired,
  enqueueSnackbar: PropTypes.func,
  viewerIsAdmin: PropTypes.bool,
  chatRoomId: PropTypes.string.isRequired,
  viewer: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onReply: PropTypes.func,
  onMention: PropTypes.func,
  onNewMessage: PropTypes.func,
};

export default withSnackbar(ChatMessageList);
